import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TradeTypeEnum } from '@olv/ui-shared/models/merchant-transaction.model';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { TextInputComponent } from '../text-input/text-input.component';
import { ButtonComponent } from '../button/button.component';

export interface RecordExchangeDialogResult {
  tradeType: TradeTypeEnum;
  date: Date;
  amount: number;
  rate: number;
  fees: number;
}

export interface RecordExchangeDialogData {
  currency: string;
  mode?: 'create' | 'edit';
  initialValue?: (Partial<RecordExchangeDialogResult> & { direction: string }) | null;
}

@Component({
  selector: 'app-record-exchange-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, TextInputComponent, DatePickerComponent, ButtonComponent],
  templateUrl: './record-exchange.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./record-exchange.component.scss'],
})
export class RecordExchangeComponent implements AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(MatDialogRef<RecordExchangeComponent, RecordExchangeDialogResult>);
  readonly dialogData = inject<RecordExchangeDialogData>(MAT_DIALOG_DATA);
  readonly TradeTypeEnum = TradeTypeEnum;
  readonly maxNumberInputLength = 9;

  readonly recordExchangeForm = this.fb.group({
    tradeType: this.fb.nonNullable.control<TradeTypeEnum>(TradeTypeEnum.Buy),
    date: this.fb.control<string | null>(null, Validators.required),
    amount: this.fb.nonNullable.control<number | null>(null, [
      Validators.required,
      Validators.maxLength(this.maxNumberInputLength),
    ]),
    rate: this.fb.nonNullable.control<number | null>(null, [
      Validators.required,
      Validators.maxLength(this.maxNumberInputLength),
    ]),
    fees: this.fb.nonNullable.control<number | null>(null, [Validators.maxLength(this.maxNumberInputLength)]),
  });

  selectedTradeDate: Date | null = null;

  private initialEditValue: RecordExchangeDialogResult | null = null;

  get isEditMode(): boolean {
    return this.dialogData.mode === 'edit';
  }

  get dialogTitle(): string {
    return this.isEditMode ? 'Edit exchange' : 'Record exchange';
  }

  get confirmButtonLabel(): string {
    return this.isEditMode ? 'Edit' : 'Confirm';
  }

  get isConfirmDisabled(): boolean {
    return this.recordExchangeForm.invalid || (this.isEditMode && !this.hasFormChanged());
  }

  get confirmButtonState(): 'disabled' | 'default' {
    return this.isConfirmDisabled ? 'disabled' : 'default';
  }

  ngAfterViewInit(): void {
    this.populateEditForm();
    this.cdr.detectChanges();
  }

  private populateEditForm(): void {
    const initialValue = this.dialogData.initialValue;

    if (!this.isEditMode || !initialValue) return;

    const normalizedInitialValue: RecordExchangeDialogResult = {
      tradeType: this.normalizeTradeType(initialValue.direction),
      date: this.normalizeDateValue(initialValue.date),
      amount: Number(initialValue.amount ?? 0),
      rate: Number(initialValue.rate ?? 0),
      fees: Number(initialValue.fees ?? (initialValue as any).totalFees ?? 0),
    };
    console.log('Normalized edit value:', normalizedInitialValue);

    this.initialEditValue = normalizedInitialValue;

    this.recordExchangeForm.setValue({
      tradeType: normalizedInitialValue.tradeType,
      date: normalizedInitialValue.date ? this.formatDateForInput(normalizedInitialValue.date) : null,
      amount: normalizedInitialValue.amount,
      rate: normalizedInitialValue.rate,
      fees: Number(initialValue.fees ?? (initialValue as any).totalFees ?? 0),
    });

    if (normalizedInitialValue.date) {
      this.selectedTradeDate = new Date(normalizedInitialValue.date);
    }

    this.recordExchangeForm.markAsPristine();
    this.recordExchangeForm.markAsUntouched();
  }

  setTradeType(tradeType: TradeTypeEnum): void {
    this.recordExchangeForm.controls.tradeType.setValue(tradeType);
  }

  onDateSelected(date: Date): void {
    this.selectedTradeDate = date;
    this.recordExchangeForm.controls.date.setValue(this.formatDateForInput(date));
    this.recordExchangeForm.controls.date.markAsDirty();
    this.recordExchangeForm.controls.date.markAsTouched();
  }

  onSubmit(): void {
    const form = this.recordExchangeForm;

    if (this.isConfirmDisabled) {
      form.markAllAsTouched();
      return;
    }

    const { tradeType, date, amount, rate, fees } = form.getRawValue();

    const result: RecordExchangeDialogResult = {
      tradeType,
      date: new Date(date!),
      amount: Number(amount),
      rate: Number(rate),
      fees: Number(fees),
    };

    this.dialogRef.close(result);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  getFieldError(controlName: 'date' | 'amount' | 'rate' | 'fees', label: string): string {
    const control = this.recordExchangeForm.get(controlName);
    if (!control || !control.invalid || !(control.touched || control.dirty)) {
      return '';
    }

    if (control.hasError('required')) {
      return `${label} is required`;
    }

    if (control.hasError('maxlength')) {
      return `${label} cannot exceed ${this.maxNumberInputLength} characters`;
    }

    return `Invalid ${label.toLowerCase()}`;
  }

  private hasFormChanged(): boolean {
    if (!this.initialEditValue) return false;

    const { tradeType, date, amount, rate, fees } = this.recordExchangeForm.getRawValue();

    return (
      tradeType !== this.initialEditValue.tradeType ||
      (date ?? '') !== this.formatDateForInput(this.initialEditValue.date) ||
      Number(amount ?? 0) !== this.initialEditValue.amount ||
      Number(rate ?? 0) !== this.initialEditValue.rate ||
      Number(fees ?? 0) !== this.initialEditValue.fees
    );
  }

  private normalizeDateValue(date: string | Date | undefined): Date {
    if (!date) return new Date();

    return date instanceof Date ? date : new Date(date);
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private normalizeTradeType(direction: string): TradeTypeEnum {
    const normalizedDirection = String(direction).trim().toUpperCase();

    if (normalizedDirection === 'SELL') {
      return TradeTypeEnum.Sell;
    } else {
      return TradeTypeEnum.Buy;
    }
  }
}
